/**
 * Phase 32 Plan 32-3 — engineering value-tag API client.
 *
 * Endpoints (Plan 32-3 / D-06 / D-07 / D-08 / D-21 / D-23):
 *   POST   /api/projects/:projectId/value-tags  (admin / PM / lead)
 *   GET    /api/users/:userId/value-tags        (any authenticated user; D-08)
 *   PATCH  /api/value-tags/:id                  (service-layer auth)
 *   DELETE /api/value-tags/:id                  (service-layer auth; soft-delete)
 *
 * Date fields are YYYY-MM-DD strings (server emits via dateToISODay).
 * Timestamps are ISO 8601.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export type ValueTagCategory =
  | 'feature'
  | 'bugfix'
  | 'refactor'
  | 'infra'
  | 'docs'
  | 'customer_request'
  | 'tech_debt'
  | 'other';

export interface ValueTag {
  id: number;
  userId: number;
  projectId: number;
  fromDate: string; // YYYY-MM-DD
  toDate: string;
  category: ValueTagCategory;
  note: string | null;
  taggedById: number;
  taggedBy: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
  project: { id: number; name: string | null };
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
  createdAt: string; // ISO 8601
  updatedAt: string;
}

export interface CreateValueTagInput {
  userId: number;
  fromDate: string;
  toDate: string;
  category: ValueTagCategory;
  note?: string | null;
}

export type UpdateValueTagInput = Partial<Omit<CreateValueTagInput, 'userId'>>;

export async function fetchValueTagsForUser(
  userId: number,
  opts?: { projectId?: number },
): Promise<ValueTag[]> {
  const qs =
    opts?.projectId !== undefined ? `?projectId=${opts.projectId}` : '';
  const res = await apiGet<{ data: ValueTag[] }>(
    `/users/${userId}/value-tags${qs}`,
  );
  return res.data;
}

export async function createValueTag(
  projectId: number,
  input: CreateValueTagInput,
): Promise<ValueTag> {
  const res = await apiPost<{ data: ValueTag }>(
    `/projects/${projectId}/value-tags`,
    input,
  );
  return res.data;
}

export async function updateValueTag(
  id: number,
  input: UpdateValueTagInput,
): Promise<ValueTag> {
  const res = await apiPatch<{ data: ValueTag }>(`/value-tags/${id}`, input);
  return res.data;
}

export async function deleteValueTag(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/value-tags/${id}`);
}
